import {
  CustomParameters,
  DeferredPromise,
  PaymentMethod,
  PaymentMethodTypes,
  PaywallApi,
  PaywallFeature,
  PaywallResult,
  PurchasableProduct,
  StoredCreditCard,
} from '@accedo/one-plugin-sdk';
import { RefObject } from 'react';
import { proxy, ref, snapshot, useSnapshot } from 'valtio';

import {
  PackageDefinition,
  ProductsResponse,
  PurchaseMethod,
} from '#/client/models';
import { devtoolsApi } from '#/dev/devtools';
import { Errors } from '#/errors/errors';
import { OneError } from '#/errors/oneError';
import {
  CardStatus,
  CloseHandler,
  ShowPaywallParams,
} from '#/plugins/paywall/models';
import { getOtherPlatformInfo } from '#/plugins/paywall/utils';
import { getDeferredPromise } from '#/service/utils';

import { authActions, authStore } from '../auth/auth.store';
import { resetStore } from '../common';
import { getExpiryStatus } from './paywall.utils';
import { ClientPaywallApi, getPaywallApiFromRef } from './paywallApi';
import {
  getPurchasableProductIdentifiers,
  ProductInfo,
} from './purchasableProductIdentifiers';

export interface Cancel {
  isSaveCampaignEnabled: boolean;
  isLoading?: boolean;
  isDone?: boolean;
  selectedProductId: string;
  saveCampaignOfferText?: string;
  saveCampaignOfferName?: string;
  userSubscriptionId?: string;
  error?: OneError;
}

export interface PaywallStore {
  paywallApi: ClientPaywallApi;
  isShowing: boolean;
  /** paywall plugin is loaded, api endpoints are available */
  isPluginReady: boolean;
  isFetching: boolean;
  hasFetched: boolean;
  supportedFeatures: PaywallFeature[];
  userProductsInvalidatedAt: string;
  expiryStatus: CardStatus | undefined;
  /** temporary values to keep between `showPaywall` and `closePaywall`.
   * Cleared after the paywall is closed, so don't use them for anything else. */
  deferredPromise?: DeferredPromise<PaywallResult>;
  customParameters?: CustomParameters;
  closeHandler?: CloseHandler;
  /** this is where we will move the temporary stuff from above.
   * Can be stored before or at the `showPaywall` call and will be cleared on `closePaywall`. */
  tempSession: {
    /** entitlements needed for the current item in case the register/login/purchase action was called on a details page. */
    purchaseEntitlements?: string[];
    /** what parameters were used to show the paywall */
    currentPaywallParams?: ShowPaywallParams;
    /** products response for the current paywall flow */
    productsResponse?: ProductsResponse;
  };
  apiRef?: RefObject<PaywallApi>;
  /** tracked states which is needed for the cancel page */
  cancel: Cancel;
  /** this error should only be used within the actual paywall flow */
  error?: OneError;
}

export const paywallStore = proxy<PaywallStore>({
  paywallApi: {
    hasOwnUi: () => false,
    getProviderInfo: () => Promise.resolve({ name: 'unknown' }),
  },
  isShowing: false,
  isPluginReady: false,
  isFetching: false,
  hasFetched: false,
  supportedFeatures: [],
  userProductsInvalidatedAt: new Date().toISOString(),
  expiryStatus: undefined,
  deferredPromise: undefined,
  customParameters: undefined,
  closeHandler: undefined,
  tempSession: {},
  apiRef: undefined,
  error: undefined,
  cancel: {
    selectedProductId: '',
    isSaveCampaignEnabled: false,
    isLoading: false,
    isDone: false,
  },
});

export const initialPaywallStore = snapshot(paywallStore) as PaywallStore;

export const paywallActions = {
  initPlugin: (apiRef: RefObject<PaywallApi>) => {
    paywallStore.apiRef = apiRef;

    return getPaywallApiFromRef(apiRef).then((api) => {
      paywallStore.paywallApi = ref(api);
      paywallStore.isPluginReady = true;

      return refreshSupportedFeatures();
    });
  },

  showPaywall: (params?: ShowPaywallParams) => {
    if (!authStore.isLoggedIn) {
      authActions.showLogin();

      return Promise.resolve(PaywallResult.Aborted);
    }

    const _deferred = getDeferredPromise<PaywallResult>();
    paywallStore.isShowing = true;
    paywallStore.deferredPromise = _deferred;

    paywallStore.tempSession.currentPaywallParams = {
      ...params,
      // if we didn't get fetchWithEntitlementTags param, we should use the temp session value instead
      fetchWithEntitlementTags:
        params?.fetchWithEntitlementTags ??
        paywallStore.tempSession.purchaseEntitlements,
    };
    paywallStore.tempSession.productsResponse = undefined;

    if (params?.closeHandler) {
      paywallStore.closeHandler = params.closeHandler;
    }

    if (params?.customParameters) {
      paywallStore.customParameters = params.customParameters;
    }

    return _deferred.promise;
  },

  closePaywall: () => {
    paywallStore.deferredPromise?.reject(PaywallResult.Aborted);
    paywallStore.deferredPromise = undefined;
    paywallStore.isShowing = false;
    paywallStore.error = undefined;
    paywallStore.customParameters = undefined;
    paywallStore.closeHandler = undefined;
    paywallStore.tempSession = {};
  },

  setCancel: (cancel: Cancel) => {
    Object.assign(paywallStore.cancel, cancel);
  },

  // This method should only be used to set errors within the cancel flow / page.
  setCancelError: (error: OneError | undefined) => {
    paywallStore.cancel.error = error;
  },

  setError: (error: OneError | undefined) => {
    paywallStore.error = error;
  },

  setPurchaseEntitlements(entitlements?: string[]) {
    paywallStore.tempSession.purchaseEntitlements = entitlements;
  },

  refreshProducts() {
    paywallStore.userProductsInvalidatedAt = new Date().toISOString();
  },

  // if the payment method is available, the caller just pass it in. otherwise, we will fetch it here.
  refreshExpiryStatus(prefetched?: PaymentMethod) {
    paywallStore.expiryStatus = undefined;

    if (!authStore.isLoggedIn || !paywallStore.paywallApi.getPaymentDetails) {
      return;
    }

    Promise.resolve(prefetched ?? paywallStore.paywallApi.getPaymentDetails())
      .then((details) => {
        const { type, properties } = details ?? {};

        if (type !== PaymentMethodTypes.CreditCard || !properties) {
          return;
        }

        const card = properties as StoredCreditCard;
        paywallStore.expiryStatus = getExpiryStatus(card.expiryDate);
      })
      .catch((err) => {
        console.warn('Failed to fetch expiry status', err);
      });
  },

  // This method allows the Integration to apply its own logic for generating a usable product identifier (e.g. Cleeng localization IDs)
  // Post-fixed value of Safely is in reference to the adapter method potentially not being available, in which case we want to default to the original incoming value.
  transformProductIdentifierSafely: (id: string = '') =>
    paywallStore.paywallApi.transformProductIdentifier?.(id) ?? id,

  reset: () => {
    resetStore(paywallStore, initialPaywallStore);
  },
};

export const usePaywallStore = () => useSnapshot(paywallStore) as PaywallStore;

export const getPurchasableProducts = async (
  params: ShowPaywallParams | undefined
): Promise<{
  purchasableProducts: PurchasableProduct[];
  packageDefinition?: PackageDefinition;
  otherPlatformError?: OneError;
}> => {
  if (!params || !paywallStore.paywallApi.getPurchasableProducts) {
    return { purchasableProducts: [] };
  }

  // get currently owned product identifiers
  const { subscriptions = [] } = authStore.entitlements;
  const currentUserProductIdentifiers = subscriptions
    .map(
      (subscription) =>
        subscription.creditCardProductDetails?.productId ??
        subscription.paypalProductDetails?.productId
    )
    .filter(Boolean);

  // collect subscription group ids from products purchased on other platforms
  const otherPlatformGroupIds = subscriptions
    .filter(
      (subscription) =>
        getOtherPlatformInfo(
          paywallStore.paywallApi,
          paywallStore.isPluginReady,
          subscription.purchaseMethod,
          subscription.purchaseMethodTranslationKey
        ).isOtherPlatform
    )
    .map((subscription) => subscription.subscriptionGroupId)
    .filter(Boolean);

  // get purchasable product identifiers
  const {
    productIdentifiers,
    productsResponse,
    otherPlatformErrorGroupId,
    productInfoMap,
  } = await getPurchasableProductIdentifiers(
    params,
    currentUserProductIdentifiers,
    otherPlatformGroupIds,
    paywallActions.transformProductIdentifierSafely
  );

  // store it for reuse while the paywall is open
  paywallStore.tempSession.productsResponse = productsResponse;

  // if there are no product identifiers and the user has a subscription group owned on another platform,
  // we return an error object that we can use higher up in flow.
  if (!productIdentifiers.length && otherPlatformErrorGroupId) {
    const purchaseMethod: PurchaseMethod =
      subscriptions.find(
        ({ subscriptionGroupId }) =>
          subscriptionGroupId === otherPlatformErrorGroupId
      )?.purchaseMethod ?? 'UNKNOWN';

    return {
      purchasableProducts: [],
      packageDefinition: productsResponse?.packageDefinition,
      otherPlatformError: new OneError(
        Errors[
          `PURCHASE_OFFERS_LOADING_${purchaseMethod}_EXISTING_SUBSCRIPTION`
        ] ?? Errors.PURCHASE_OFFERS_LOADING_UNKNOWN_EXISTING_SUBSCRIPTION
      ),
    };
  }

  if (!productIdentifiers.length) {
    return {
      purchasableProducts: [],
      packageDefinition: productsResponse?.packageDefinition,
    };
  }

  // get details for each purchasable product identifier
  const purchasableProducts =
    await paywallStore.paywallApi.getPurchasableProducts(productIdentifiers);

  return {
    purchasableProducts: mergePackageDefinitionNames(
      purchasableProducts,
      productInfoMap
    ),
    packageDefinition: productsResponse?.packageDefinition,
  };
};

/**
 * Merge the product name from the package definition into the purchasable products, so that it can be used in the UI. It should take precedence over the title from the getPurchasableProducts API response.
 * @param purchasableProducts The list of purchasable products.
 * @param productInfoMap A map of product information from the package definition.
 * @returns A new list of purchasable products with names from the package definition.
 */
export const mergePackageDefinitionNames = (
  purchasableProducts: PurchasableProduct[],
  productInfoMap: Record<string, ProductInfo>
): PurchasableProduct[] => {
  return purchasableProducts.map((product) => {
    const id = paywallActions.transformProductIdentifierSafely(product.id);
    const name = productInfoMap[id]?.name;

    return {
      ...product,
      ...(name && { title: name }),
    };
  });
};

const refreshSupportedFeatures = async () => {
  if (paywallStore.paywallApi.supportedFeatures) {
    return paywallStore.paywallApi
      .supportedFeatures()
      .then((features) => {
        paywallStore.supportedFeatures = features;
      })
      .catch((error) => {
        paywallStore.error = error;
        paywallStore.supportedFeatures = Object.values(PaywallFeature);
      });
  }

  // According to the existing logic, if the API doesn't support `supportedFeatures` method,
  // it means that all features are supported.
  paywallStore.supportedFeatures = Object.values(PaywallFeature);
};

devtoolsApi.setStore('paywall', paywallStore, paywallActions);
